import React, { useEffect, useState, useRef, createElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, MoreHorizontalIcon, UsersIcon, FileTextIcon, PlusIcon, LinkIcon, CheckIcon, CopyIcon, ArchiveIcon, DownloadIcon, MoreVerticalIcon, UserXIcon, EyeIcon, EyeOffIcon, SearchIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, BellIcon } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import EmptyState from '../components/EmptyState';
import { AddLearnersModal } from '../components/AddLearnersModal';
import { EditClassroomModal } from '../components/EditClassroomModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { StudentProfilePanel } from '../components/StudentProfilePanel';
// Mock Data
const LEARNERS_DATA = [{
  id: 1,
  name: 'Alice Johnson',
  email: 'alice@school.edu',
  status: 'Joined',
  joinedAt: '2024-08-15',
  guardianName: 'Mary Johnson',
  guardianPhone: '+1 (555) 123-4567',
  guardianInfoStatus: 'complete' as const
}, {
  id: 2,
  name: 'Bob Smith',
  email: 'bob@school.edu',
  status: 'Invited',
  joinedAt: '-',
  guardianName: 'Sarah Smith',
  guardianPhone: '+1 (555) 234-5678',
  guardianInfoStatus: 'complete' as const
}, {
  id: 3,
  name: 'Charlie Brown',
  email: 'charlie@school.edu',
  status: 'Joined',
  joinedAt: '2024-08-16',
  guardianName: null,
  guardianPhone: null,
  guardianInfoStatus: 'pending-reminder' as const
}, {
  id: 4,
  name: 'Diana Martinez',
  email: 'diana.m@school.edu',
  status: 'Joined',
  joinedAt: '2024-08-17',
  guardianName: null,
  guardianPhone: null,
  guardianInfoStatus: 'incomplete' as const
}];
export function ClassroomDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'learners' | 'activity'>('learners');
  const [isAddLearnerOpen, setIsAddLearnerOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [classroomData, setClassroomData] = useState({
    id: '1',
    name: 'Grade 7A',
    grade: 'Grade 7',
    section: 'A',
    academicYear: '2024-2025',
    joinCode: 'XY7-992',
    stats: {
      learners: 24,
      assignments: 5
    }
  });
  const [linkCopied, setLinkCopied] = useState(false);
  const [studentToRemove, setStudentToRemove] = useState<any>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showGuardianInfo, setShowGuardianInfo] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [showStudentPanel, setShowStudentPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const moreMenuRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };
    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreMenuOpen]);
  const handleEditClassroom = async (data: any) => {
    console.log('Updating classroom:', data);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setClassroomData(prev => ({
      ...prev,
      name: data.name,
      grade: data.grade,
      section: data.section
    }));
    setIsEditModalOpen(false);
  };
  const handleCopyJoinLink = () => {
    const link = `https://app.ainstein.com/join/${classroomData.joinCode}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  const handleExportRoster = () => {
    console.log('Exporting roster...');
    // Create CSV content
    const csvContent = 'Name,Email,Status,Joined At\n' + LEARNERS_DATA.map(l => `${l.name},${l.email},${l.status},${l.joinedAt}`).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${classroomData.name}_roster.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsMoreMenuOpen(false);
  };
  const handleArchiveClassroom = async () => {
    console.log('Archiving classroom...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    navigate('/organization/classrooms');
  };
  const handleStudentClick = (student: any) => {
    // Convert learner data to student format for the profile panel
    const studentData = {
      id: student.id.toString(),
      name: student.name,
      email: student.email,
      status: student.status === 'Joined' ? 'enrolled' as const : 'not-enrolled' as const,
      joinedAt: student.joinedAt,
      guardian: student.guardianName ? {
        id: `g${student.id}`,
        name: student.guardianName,
        phone: student.guardianPhone
      } : undefined
    };
    setSelectedStudent(studentData);
    setShowStudentPanel(true);
  };
  const handleEndEnrollment = (student: any) => {
    setStudentToRemove(student);
    setShowRemoveConfirm(true);
    setShowStudentPanel(false);
  };
  const confirmEndEnrollment = async () => {
    console.log('Ending enrollment for:', studentToRemove);
    await new Promise(resolve => setTimeout(resolve, 500));
    setShowRemoveConfirm(false);
    setStudentToRemove(null);
    setShowStudentPanel(false);
  };
  const formatDate = (dateString: string) => {
    if (dateString === '-') return 'Not yet joined';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };
  // Filter students based on search query
  const filteredLearners = LEARNERS_DATA.filter(learner => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return learner.name.toLowerCase().includes(query) || learner.email.toLowerCase().includes(query) || learner.guardianName?.toLowerCase().includes(query) || learner.guardianPhone?.toLowerCase().includes(query);
  });
  return <div className="flex flex-col min-h-screen bg-background" data-id="element-2572">
      <Header data-id="element-2573" />
      <div className="flex flex-1 overflow-hidden" data-id="element-2574">
        <div className="hidden md:block" data-id="element-2575">
          <Sidebar data-id="element-2576" />
        </div>
        <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-2577">
          <main className="flex-1" data-id="element-2578">
            {/* Breadcrumb & Header */}
            <div className="bg-white border-b border-custom px-4 sm:px-6 py-3 sm:py-4" data-id="element-2579">
              <div className="max-w-7xl mx-auto" data-id="element-2580">
                <button onClick={() => navigate('/organization/classrooms')} className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors mb-3" data-id="element-2581">
                  <ArrowLeftIcon size={16} data-id="element-2582" />
                  <span className="hidden sm:inline" data-id="element-2583">Back to Classrooms</span>
                  <span className="sm:hidden" data-id="element-2584">Back</span>
                </button>

                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3" data-id="element-2585">
                  <div className="flex-1" data-id="element-2586">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2" data-id="element-2587">
                      <h1 className="text-xl sm:text-2xl font-bold text-text-primary" data-id="element-2588">
                        {classroomData.name}
                      </h1>
                      <span className="px-2 py-0.5 rounded-lg text-xs font-bold bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20 uppercase tracking-wide w-fit" data-id="element-2589">
                        Organization Classroom
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary mb-3" data-id="element-2590">
                      <span data-id="element-2591">{classroomData.grade}</span>
                      <span data-id="element-2592">•</span>
                      <span data-id="element-2593">Section {classroomData.section}</span>
                      <span data-id="element-2594">•</span>
                      <span className="font-medium text-text-primary" data-id="element-2595">
                        {classroomData.academicYear}
                      </span>
                    </div>

                    {/* Stats Cards - Inline Compact */}
                    <div className="flex items-center gap-4 text-sm" data-id="element-2596">
                      <div className="flex items-center gap-2" data-id="element-2597">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-2598">
                          <UsersIcon size={14} data-id="element-2599" />
                        </div>
                        <div data-id="element-2600">
                          <p className="text-xs text-text-tertiary" data-id="element-2601">
                            Enrolled Students
                          </p>
                          <p className="text-lg font-bold text-text-primary" data-id="element-2602">
                            {classroomData.stats.learners}
                          </p>
                        </div>
                      </div>

                      <div className="w-px h-8 bg-border" data-id="element-2603"></div>

                      <div className="flex items-center gap-2" data-id="element-2604">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center" data-id="element-2605">
                          <FileTextIcon size={14} data-id="element-2606" />
                        </div>
                        <div data-id="element-2607">
                          <p className="text-xs text-text-tertiary" data-id="element-2608">
                            Active Assignments
                          </p>
                          <p className="text-lg font-bold text-text-primary" data-id="element-2609">
                            {classroomData.stats.assignments}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 lg:flex-shrink-0" data-id="element-2610">
                    <button onClick={() => setIsEditModalOpen(true)} className="flex-1 sm:flex-none px-3 py-1.5 border border-custom rounded-lg text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors whitespace-nowrap" data-id="element-2611">
                      <span className="hidden sm:inline" data-id="element-2612">Edit Details</span>
                      <span className="sm:hidden" data-id="element-2613">Edit</span>
                    </button>

                    {/* More Menu */}
                    <div className="relative" ref={moreMenuRef} data-id="element-2614">
                      <button onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)} className="p-1.5 border border-custom rounded-lg text-text-secondary hover:bg-gray-50 transition-colors" aria-label="More options" data-id="element-2615">
                        <MoreHorizontalIcon size={20} data-id="element-2616" />
                      </button>

                      <AnimatePresence data-id="element-2617">
                        {isMoreMenuOpen && <motion.div initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className="absolute right-0 mt-2 w-56 bg-white border border-custom rounded-xl shadow-lg overflow-hidden z-50" data-id="element-2618">
                            <button onClick={handleExportRoster} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-text-primary hover:bg-gray-50 transition-colors" data-id="element-2619">
                              <DownloadIcon size={16} data-id="element-2620" />
                              Export Roster
                            </button>
                            <button onClick={handleCopyJoinLink} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-text-primary hover:bg-gray-50 transition-colors border-t border-gray-100" data-id="element-2621">
                              <LinkIcon size={16} data-id="element-2622" />
                              Copy Join Link
                            </button>
                            <button onClick={() => {
                          setIsArchiveModalOpen(true);
                          setIsMoreMenuOpen(false);
                        }} className="w-full px-4 py-3 flex items-center gap-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100" title="Close classroom for this academic year. Student records and enrollments are preserved." data-id="element-2623">
                              <ArchiveIcon size={16} data-id="element-2624" />
                              Archive Classroom
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                {/* Tab Navigation - Compact */}
                <div className="flex items-center gap-6 mt-4 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 scrollbar-hide" data-id="element-2625">
                  {[{
                  id: 'learners',
                  label: 'Enrolled Students'
                }, {
                  id: 'activity',
                  label: 'Activity'
                }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`pb-2 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === tab.id ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-2626">
                      {tab.label}
                      {activeTab === tab.id && <motion.div layoutId="activeTabDetail" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-2627" />}
                    </button>)}
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5" data-id="element-2628">
              {/* Learners Tab */}
              {activeTab === 'learners' && <div data-id="element-2629">
                  <div className="flex flex-col gap-3 mb-4" data-id="element-2630">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-2631">
                      <div className="flex-1" data-id="element-2632">
                        <h2 className="text-base sm:text-lg font-bold text-text-primary" data-id="element-2633">
                          Enrolled Students
                        </h2>
                        <p className="text-xs text-text-secondary" data-id="element-2634">
                          {filteredLearners.length} of {LEARNERS_DATA.length}{' '}
                          students
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap" data-id="element-2635">
                        <button onClick={() => setShowGuardianInfo(!showGuardianInfo)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-text-tertiary hover:bg-gray-50 hover:text-text-secondary transition-colors flex items-center gap-1.5 whitespace-nowrap" data-id="element-2636">
                          {showGuardianInfo ? <>
                              <EyeOffIcon size={14} data-id="element-2637" />
                              <span className="hidden sm:inline" data-id="element-2638">
                                Hide Guardian Info
                              </span>
                              <span className="sm:hidden" data-id="element-2639">Hide Guardian</span>
                            </> : <>
                              <EyeIcon size={14} data-id="element-2640" />
                              <span className="hidden sm:inline" data-id="element-2641">
                                Show Guardian Info
                              </span>
                              <span className="sm:hidden" data-id="element-2642">Show Guardian</span>
                            </>}
                        </button>
                        <button onClick={() => setIsAddLearnerOpen(true)} className="flex-1 sm:flex-none px-3 py-1.5 bg-[#00B75F] text-white rounded-lg text-sm font-semibold hover:bg-[#00a055] transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-2643">
                          <LinkIcon size={16} data-id="element-2644" />
                          <span className="hidden sm:inline" data-id="element-2645">
                            Share Invite Link
                          </span>
                          <span className="sm:hidden" data-id="element-2646">Share Link</span>
                        </button>
                      </div>
                    </div>

                    {/* Search Bar */}
                    <div className="relative" data-id="element-2647">
                      <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-2648" />
                      <input type="text" placeholder="Search by name, email, or guardian..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" data-id="element-2649" />
                    </div>
                  </div>

                  {filteredLearners.length > 0 ? <>
                      {/* Desktop Table View - Compact */}
                      <div className="hidden md:block bg-white border border-custom rounded-xl overflow-hidden" data-id="element-2650">
                        <div className="overflow-x-auto" data-id="element-2651">
                          <table className="w-full" data-id="element-2652">
                            <thead className="bg-gray-50 border-b border-custom" data-id="element-2653">
                              <tr data-id="element-2654">
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2655">
                                  Name
                                </th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2656">
                                  Email
                                </th>
                                {showGuardianInfo && <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2657">
                                    Guardian Contact
                                  </th>}
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2658">
                                  Joined
                                </th>
                                <th className="px-4 py-2.5 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-2659">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100" data-id="element-2660">
                              {filteredLearners.map(learner => <tr key={learner.id} onClick={() => handleStudentClick(learner)} className="hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2661">
                                  <td className="px-4 py-2.5" data-id="element-2662">
                                    <span className="text-sm font-medium text-text-primary" data-id="element-2663">
                                      {learner.name}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-sm text-text-secondary" data-id="element-2664">
                                    {learner.email}
                                  </td>
                                  {showGuardianInfo && <td className="px-4 py-2.5" data-id="element-2665">
                                      {learner.guardianPhone ? <p className="text-sm text-text-secondary" data-id="element-2666">
                                          {learner.guardianPhone}
                                        </p> : <span className="text-sm text-text-tertiary" data-id="element-2667">
                                          —
                                        </span>}
                                    </td>}
                                  <td className="px-4 py-2.5 text-sm text-text-secondary" data-id="element-2668">
                                    {formatDate(learner.joinedAt)}
                                  </td>
                                  <td className="px-4 py-2.5 text-right" data-id="element-2669">
                                    <DropdownMenu.Root data-id="element-2670">
                                      <DropdownMenu.Trigger asChild data-id="element-2671">
                                        <button onClick={e => e.stopPropagation()} className="p-1.5 hover:bg-surface rounded-lg transition-colors" aria-label="Student actions" data-id="element-2672">
                                          <MoreVerticalIcon size={18} className="text-text-secondary" data-id="element-2673" />
                                        </button>
                                      </DropdownMenu.Trigger>
                                      <DropdownMenu.Portal data-id="element-2674">
                                        <DropdownMenu.Content className="min-w-[180px] bg-surface-elevated rounded-xl border border-custom shadow-custom-lg p-1 z-50" sideOffset={5} align="end" onClick={e => e.stopPropagation()} data-id="element-2675">
                                          <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={() => handleStudentClick(learner)} data-id="element-2676">
                                            <UsersIcon size={16} data-id="element-2677" /> View Profile
                                          </DropdownMenu.Item>
                                          <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 cursor-pointer outline-none" onSelect={() => handleEndEnrollment(learner)} data-id="element-2678">
                                            <UserXIcon size={16} data-id="element-2679" /> End
                                            Enrollment
                                          </DropdownMenu.Item>
                                        </DropdownMenu.Content>
                                      </DropdownMenu.Portal>
                                    </DropdownMenu.Root>
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Mobile Card View - Compact */}
                      <div className="md:hidden space-y-2" data-id="element-2680">
                        {filteredLearners.map(learner => <div key={learner.id} onClick={() => handleStudentClick(learner)} className="bg-white border border-custom rounded-xl p-3 hover:border-[#00B75F] transition-colors cursor-pointer" data-id="element-2681">
                            <div className="flex items-start justify-between gap-3" data-id="element-2682">
                              <div className="flex-1 min-w-0" data-id="element-2683">
                                <h3 className="text-sm font-semibold text-text-primary truncate" data-id="element-2684">
                                  {learner.name}
                                </h3>
                                <p className="text-xs text-text-secondary truncate" data-id="element-2685">
                                  {learner.email}
                                </p>
                                <p className="text-xs text-text-tertiary mt-1" data-id="element-2686">
                                  Joined: {formatDate(learner.joinedAt)}
                                </p>
                              </div>
                              <DropdownMenu.Root data-id="element-2687">
                                <DropdownMenu.Trigger asChild data-id="element-2688">
                                  <button onClick={e => e.stopPropagation()} className="p-1.5 hover:bg-surface rounded-lg transition-colors flex-shrink-0" aria-label="Student actions" data-id="element-2689">
                                    <MoreVerticalIcon size={18} className="text-text-secondary" data-id="element-2690" />
                                  </button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Portal data-id="element-2691">
                                  <DropdownMenu.Content className="min-w-[180px] bg-surface-elevated rounded-xl border border-custom shadow-custom-lg p-1 z-50" sideOffset={5} align="end" onClick={e => e.stopPropagation()} data-id="element-2692">
                                    <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-text-primary rounded-lg hover:bg-surface cursor-pointer outline-none" onSelect={() => handleStudentClick(learner)} data-id="element-2693">
                                      <UsersIcon size={16} data-id="element-2694" /> View Profile
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 cursor-pointer outline-none" onSelect={() => handleEndEnrollment(learner)} data-id="element-2695">
                                      <UserXIcon size={16} data-id="element-2696" /> End Enrollment
                                    </DropdownMenu.Item>
                                  </DropdownMenu.Content>
                                </DropdownMenu.Portal>
                              </DropdownMenu.Root>
                            </div>

                            {showGuardianInfo && learner.guardianName && <div className="mt-2 pt-2 border-t border-custom space-y-1" data-id="element-2697">
                                {learner.guardianPhone ? <p className="text-sm text-text-secondary" data-id="element-2698">
                                    {learner.guardianPhone}
                                  </p> : <span className="text-sm text-text-tertiary" data-id="element-2699">
                                    —
                                  </span>}
                              </div>}

                            {/* Guardian Status Badge */}
                            <div className="mt-2 pt-2 border-t border-custom" data-id="element-2700">
                              {learner.guardianInfoStatus === 'complete' ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#00B75F] bg-opacity-10 text-[#00B75F]" data-id="element-2701">
                                  <CheckCircleIcon size={10} data-id="element-2702" />
                                  Guardian Info Complete
                                </span> : learner.guardianInfoStatus === 'pending-reminder' ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]" data-id="element-2703">
                                  <BellIcon size={10} data-id="element-2704" />
                                  Reminder Sent
                                </span> : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-text-tertiary" data-id="element-2705">
                                  <AlertCircleIcon size={10} data-id="element-2706" />
                                  Guardian Info Incomplete
                                </span>}
                            </div>
                          </div>)}
                      </div>
                    </> : <div className="text-center py-12 bg-surface-elevated rounded-xl border border-custom" data-id="element-2707">
                      <SearchIcon size={48} className="mx-auto text-text-tertiary mb-4" data-id="element-2708" />
                      <h3 className="text-lg font-bold text-text-primary mb-2" data-id="element-2709">
                        No students found
                      </h3>
                      <p className="text-text-secondary mb-4" data-id="element-2710">
                        {searchQuery ? `No students match "${searchQuery}"` : 'No students enrolled yet'}
                      </p>
                      {searchQuery && <button onClick={() => setSearchQuery('')} className="px-4 py-2 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors" data-id="element-2711">
                          Clear Search
                        </button>}
                      {!searchQuery && <button onClick={() => setIsAddLearnerOpen(true)} className="px-4 py-2 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors" data-id="element-2712">
                          Share Invite Link
                        </button>}
                    </div>}
                </div>}

              {/* Activity Tab */}
              {activeTab === 'activity' && <EmptyState icon={<FileTextIcon size={48} className="text-text-tertiary" data-id="element-2714" />} title="No activity yet" description="Assessments assigned to this classroom will appear here." data-id="element-2713" />}
            </div>
          </main>
          <Footer data-id="element-2715" />
        </div>
      </div>

      <AddLearnersModal isOpen={isAddLearnerOpen} onClose={() => setIsAddLearnerOpen(false)} classroomName={classroomData.name} joinCode={classroomData.joinCode} data-id="element-2716" />

      <EditClassroomModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleEditClassroom} classroom={{
      name: classroomData.name,
      grade: classroomData.grade,
      section: classroomData.section
    }} data-id="element-2717" />

      <ConfirmationModal isOpen={isArchiveModalOpen} onClose={() => setIsArchiveModalOpen(false)} onConfirm={handleArchiveClassroom} title="Archive Classroom?" description="This classroom will be archived and closed for the current academic year. All student enrollments will be ended, but student records, enrollment history, and reports are permanently preserved. Students can be re-enrolled in future classrooms." confirmLabel="Archive Classroom" type="warning" data-id="element-2718" />

      <ConfirmationModal isOpen={showRemoveConfirm} onClose={() => {
      setShowRemoveConfirm(false);
      setStudentToRemove(null);
    }} onConfirm={confirmEndEnrollment} title="End Enrollment" description={<div className="space-y-4" data-id="element-2720">
            <p className="text-text-secondary" data-id="element-2721">
              This will remove the learner from this classroom for the current
              academic year. The learner account and history will remain
              preserved.
            </p>
            <div className="bg-surface rounded-lg p-4 border border-custom space-y-2" data-id="element-2722">
              <h4 className="text-sm font-semibold text-text-primary mb-3" data-id="element-2723">
                Context:
              </h4>
              <div className="space-y-2 text-sm" data-id="element-2724">
                <div className="flex justify-between" data-id="element-2725">
                  <span className="text-text-tertiary" data-id="element-2726">Learner:</span>
                  <span className="text-text-primary font-medium" data-id="element-2727">
                    {studentToRemove?.name}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2728">
                  <span className="text-text-tertiary" data-id="element-2729">Classroom:</span>
                  <span className="text-text-primary font-medium" data-id="element-2730">
                    {classroomData.name}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-2731">
                  <span className="text-text-tertiary" data-id="element-2732">Academic Year:</span>
                  <span className="text-text-primary font-medium" data-id="element-2733">
                    {classroomData.academicYear}
                  </span>
                </div>
              </div>
            </div>
          </div>} confirmLabel="End Enrollment" type="warning" data-id="element-2719" />

      {/* Student Profile Panel */}
      <StudentProfilePanel isOpen={showStudentPanel} student={selectedStudent} onClose={() => setShowStudentPanel(false)} onEndEnrollment={handleEndEnrollment} currentClassroom={{
      name: classroomData.name,
      academicYear: classroomData.academicYear
    }} showEndEnrollment={true} data-id="element-2734" />
    </div>;
}